import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '../../config/AppConfigContext';
import type { CreateUserFormData } from '../../types/voice-demo';
import { PARTNER_USERS_PATH } from '@hooks/requests/utils/paths';

const joinUrl = (baseUrl: string, path: string) => {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

export const useCreateUserMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserFormData) => {
      if (!allyApi) throw new Error('Missing allyApi configuration');

      const token = auth0Audience
        ? await getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
        : await getAccessTokenSilently();

      const body: Record<string, string> = {
        userId: data.userId,
        firstName: data.firstName,
        companyId: data.companyId,
        companyName: data.companyName,
        language: data.language,
        country: data.country,
      };

      const response = await fetch(joinUrl(allyApi, PARTNER_USERS_PATH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || `HTTP ${response.status}`);
      }

      return response.json();
    },
    onSuccess: () => {
      // useAllUsersQuery's cache key is [PARTNER_USERS_PATH, params] — this
      // matches as a prefix, invalidating all cached param combinations.
      void queryClient.invalidateQueries({ queryKey: [PARTNER_USERS_PATH] });
    },
  });
};
